"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FileUtilities = void 0;
/* --------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
* Licensed under the MIT License. See License.txt in the project root for license information.
* ------------------------------------------------------------------------------------------ */
const child_process_1 = require("child_process");
const crypto = require("crypto");
const eol = require("eol");
const fs = require("fs");
const os = require("os");
const path = require("path");
const util_1 = require("util");
const CacheTimeConstants_1 = require("../Acquisition/CacheTimeConstants");
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const CommandExecutor_1 = require("./CommandExecutor");
const IFileUtilities_1 = require("./IFileUtilities");
const TypescriptUtilities_1 = require("./TypescriptUtilities");
class FileUtilities extends IFileUtilities_1.IFileUtilities {
    writeFileOntoDisk(scriptContent, filePath, eventStream) {
        return __awaiter(this, void 0, void 0, function* () {
            eventStream === null || eventStream === void 0 ? void 0 : eventStream.post(new EventStreamEvents_1.DotnetFileWriteRequestEvent(`Request to write ${filePath}`, new Date().toISOString(), filePath));
            if (!(yield this.exists(path.dirname(filePath)))) {
                yield fs.promises.mkdir(path.dirname(filePath), { recursive: true });
            }
            if (!(fs.existsSync(filePath))) {
                fs.writeFileSync(filePath, '');
            }
            yield this.innerWriteFile(scriptContent, filePath, eventStream);
        });
    }
    innerWriteFile(scriptContent, filePath, eventStream) {
        return __awaiter(this, void 0, void 0, function* () {
            scriptContent = eol.auto(scriptContent);
            const existingScriptContent = yield this.read(filePath);
            // fs.writeFile will replace the file if it exists.
            // https://nodejs.org/api/fs.html#fswritefilefile-data-options-callback
            if (scriptContent !== existingScriptContent) {
                yield fs.promises.writeFile(filePath, scriptContent);
                eventStream === null || eventStream === void 0 ? void 0 : eventStream.post(new EventStreamEvents_1.DotnetFileWriteRequestEvent(`File content needed to be updated.`, new Date().toISOString(), filePath));
            }
            else {
                eventStream === null || eventStream === void 0 ? void 0 : eventStream.post(new EventStreamEvents_1.DotnetFileWriteRequestEvent(`File content is an exact match, not writing file.`, new Date().toISOString(), filePath));
            }
            yield fs.promises.chmod(filePath, 0o744);
        });
    }
    /**
     * @param directoryToWipe the directory to delete all of the files in if privilege to do so exists.
     * @param fileExtensionsToDelete - if undefined, delete all files. if not, delete only files with extensions in this array in lower case.
     */
    wipeDirectory(directoryToWipe_1, eventStream_1, fileExtensionsToDelete_1) {
        return __awaiter(this, arguments, void 0, function* (directoryToWipe, eventStream, fileExtensionsToDelete, verifyDotnetNotInUse = false) {
            if (!(yield this.exists(directoryToWipe))) {
                eventStream === null || eventStream === void 0 ? void 0 : eventStream.post(new EventStreamEvents_1.EmptyDirectoryToWipe(`The directory ${directoryToWipe} did not exist, so it was not wiped.`));
                return;
            }
            else if (verifyDotnetNotInUse && (yield FileUtilities.fileIsOpen(path.join(directoryToWipe, (0, TypescriptUtilities_1.getDotnetExecutable)()), eventStream))) {
                return;
            }
            const directoryEntries = yield fs.promises.readdir(directoryToWipe);
            for (const entry of directoryEntries) {
                try {
                    eventStream === null || eventStream === void 0 ? void 0 : eventStream.post(new EventStreamEvents_1.FileToWipe(`Path ${entry} may be deleted.`));
                    if (!fileExtensionsToDelete || (fileExtensionsToDelete === null || fileExtensionsToDelete === void 0 ? void 0 : fileExtensionsToDelete.includes(path.extname(entry).toLocaleLowerCase()))) {
                        eventStream === null || eventStream === void 0 ? void 0 : eventStream.post(new EventStreamEvents_1.FileToWipe(`Path  ${entry} is being deleted -- if no error is reported, it should be wiped.`));
                        yield fs.promises.rm(path.join(directoryToWipe, entry), { recursive: true, force: true });
                    }
                }
                catch (error) {
                    eventStream === null || eventStream === void 0 ? void 0 : eventStream.post(new EventStreamEvents_1.SuppressedAcquisitionError(error, `Failed to delete ${entry} when marked for deletion.`));
                }
            }
            ;
        });
    }
    read(filePath) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const output = yield fs.promises.readFile(filePath, 'utf8');
                return output;
            }
            catch (error) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                throw new Error(`Failed to read file ${filePath}: ${error === null || error === void 0 ? void 0 : error.message}`);
            }
        });
    }
    exists(filePath) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield fs.promises.stat(filePath);
                return true;
            }
            catch (_a) {
                return false;
            }
        });
    }
    realpath(filePath) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const resolvedRealPath = yield (0, util_1.promisify)(fs.realpath)(filePath);
                return resolvedRealPath;
            }
            catch (_a) {
                return null;
            }
        });
    }
    /**
     *
     * @param nodeArchitecture the architecture in node style string of what to install
     * @returns the architecture in the style that .net / the .net install scripts expect
     *
     * Node - amd64 is documented as an option for install scripts but its no longer used.
     * s390x is also no longer used.
     * ppc64le is supported but this version of node has no distinction of the endianness of the process.
     * It has no mapping to mips or other node architectures.
     *
     * @remarks Falls back to string 'auto' if a mapping does not exist which is not a valid architecture.
     */
    nodeArchToDotnetArch(nodeArchitecture, eventStream) {
        switch (nodeArchitecture) {
            case 'x64': {
                return nodeArchitecture;
            }
            case 'ia32': {
                return 'x86';
            }
            case 'x86': {
                // In case the function is called twice
                return 'x86';
            }
            case 'arm': {
                return nodeArchitecture;
            }
            case 'arm64': {
                return nodeArchitecture;
            }
            case 's390x': {
                return 's390x';
            }
            default: {
                eventStream.post(new EventStreamEvents_1.DotnetCommandFallbackArchitectureEvent(`The architecture ${os.arch()} of the platform is unexpected, falling back to auto-arch.`));
                return 'auto';
            }
        }
    }
    /**
     *
     * @param nodeArchitecture the architecture output of dotnet --info from the runtime
     * @returns the architecture in the style that node expects
     *
     * @remarks Falls back to string 'auto' if a mapping does not exist which is not a valid architecture.
     * So far, the outputs are actually all identical so this is not really 'needed' but good to have in the future :)
     */
    static dotnetInfoArchToNodeArch(dotnetInfoArch, eventStream) {
        switch (dotnetInfoArch) {
            case 'x64': {
                return dotnetInfoArch;
            }
            case 'x86': {
                // In case the function is called twice
                return dotnetInfoArch;
            }
            case 'arm': { // This shouldn't be an output yet, but its possible in the future
                return dotnetInfoArch;
            }
            case 'arm64': {
                return dotnetInfoArch;
            }
            default: {
                eventStream.post(new EventStreamEvents_1.DotnetCommandFallbackArchitectureEvent(`The architecture ${dotnetInfoArch} of the platform is unexpected, falling back to auto-arch.`));
                return 'auto';
            }
        }
    }
    /**
     *
     * @param nodeOS the OS in node style string of what to install
     * @returns the OS in the style that .net / the .net install scripts expect
     *
     */
    nodeOSToDotnetOS(nodeOS, eventStream) {
        switch (nodeOS) {
            case 'win32': {
                return 'win';
            }
            case 'darwin': {
                return 'osx';
            }
            case 'linux': {
                return nodeOS;
            }
            default: {
                eventStream.post(new EventStreamEvents_1.DotnetCommandFallbackOSEvent(`The OS ${os.platform()} of the platform is unexpected, falling back to auto-os.`));
                return 'auto';
            }
        }
    }
    static fileIsOpen(filePath, eventStream) {
        return __awaiter(this, void 0, void 0, function* () {
            let fileHandle = null;
            if (os.platform() === 'win32') {
                try {
                    // eslint-disable-next-line no-bitwise
                    fileHandle = yield fs.promises.open(filePath, fs.constants.O_RDONLY | 0x10000000); // 0x10000000 is the FILE_FLAG_NO_BUFFERING flag to try to hold an exclusive 'lock'
                }
                catch (error) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    if ((error === null || error === void 0 ? void 0 : error.code) === 'EBUSY') {
                        eventStream === null || eventStream === void 0 ? void 0 : eventStream.post(new EventStreamEvents_1.FileIsBusy(`The file ${filePath} is busy due to another file handle`));
                        return true;
                    }
                }
                finally {
                    yield (fileHandle === null || fileHandle === void 0 ? void 0 : fileHandle.close());
                }
                eventStream === null || eventStream === void 0 ? void 0 : eventStream.post(new EventStreamEvents_1.FileIsNotBusy(`The file ${filePath} is not busy due to another file handle`));
                return false;
            }
            else {
                try {
                    return (0, util_1.promisify)(child_process_1.exec)(`lsof ${filePath}`).then(fulfilled => {
                        var _a;
                        const lines = (_a = fulfilled === null || fulfilled === void 0 ? void 0 : fulfilled.stdout) === null || _a === void 0 ? void 0 : _a.toString().split('\n');
                        if (lines.length > 1) // lsof returns a header line and then the lines of open files
                         {
                            eventStream === null || eventStream === void 0 ? void 0 : eventStream.post(new EventStreamEvents_1.FileIsBusy(`The file ${filePath} is busy due to another file handle as lsof shows`));
                            return Promise.resolve(true);
                        }
                        else {
                            eventStream === null || eventStream === void 0 ? void 0 : eventStream.post(new EventStreamEvents_1.FileIsBusy(`The file ${filePath} is busy due to another file handle as lsof is empty`));
                            return Promise.resolve(false);
                        }
                    }, rejected => {
                        var _a;
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        if (((_a = rejected === null || rejected === void 0 ? void 0 : rejected.code) === null || _a === void 0 ? void 0 : _a.toString()) === 'EACCESS') {
                            eventStream === null || eventStream === void 0 ? void 0 : eventStream.post(new EventStreamEvents_1.FileIsBusy(`The file ${filePath} is presumed busy due to EACCESS`));
                            return Promise.resolve(true);
                        }
                        return Promise.resolve(false); // lsof returns a non-zero exit code if the file is not open by any process. EACCESS is thrown if the user does not have permission to run lsof on that file.
                        // ENOENT or others may be thrown if the file DNE, but we only care if its open.
                    }).finally(() => { return false; });
                }
                catch (error) {
                    eventStream === null || eventStream === void 0 ? void 0 : eventStream.post(new EventStreamEvents_1.SuppressedAcquisitionError(error, `Failed to check if file ${filePath} is open.`));
                    return false;
                }
            }
        });
    }
    /**
     *
     * @returns true if the process is running with admin privileges
     */
    isElevated(context, utilContext) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const executor = new CommandExecutor_1.CommandExecutor(context, utilContext);
            if (os.platform() !== 'win32') {
                try {
                    const commandResult = yield executor.execute(CommandExecutor_1.CommandExecutor.makeCommand('id', ['-u']), { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.SYSTEM_INFORMATION_CACHE_DURATION_MS }, false);
                    return commandResult.status === '0';
                }
                catch (error) {
                    (_a = context.eventStream) === null || _a === void 0 ? void 0 : _a.post(new EventStreamEvents_1.SuppressedAcquisitionError(error, `Failed to run 'id' to check for privilege, running without privilege.`));
                    return false;
                }
            }
            try {
                // If we can execute this command on Windows then we have admin rights.
                const _ = yield executor.execute(CommandExecutor_1.CommandExecutor.makeCommand('net', ['session']), { 'stdio': 'ignore', dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.SYSTEM_INFORMATION_CACHE_DURATION_MS });
                return true;
            }
            catch (error) {
                (_b = context.eventStream) === null || _b === void 0 ? void 0 : _b.post(new EventStreamEvents_1.SuppressedAcquisitionError(error, `Failed to run 'net' to check for privilege, running without privilege.`));
                return false;
            }
        });
    }
    sha512Hasher(filePath) {
        return new Promise((resolve, reject) => {
            const hash = crypto.createHash('sha512');
            const fileStream = fs.createReadStream(filePath);
            fileStream.on('error', err => reject(err));
            fileStream.on('data', chunk => hash.update(chunk));
            fileStream.on('end', () => resolve(hash.digest('hex')));
        });
    }
    ;
    getFileHash(filePath) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield this.sha512Hasher(filePath);
            return res;
        });
    }
}
exports.FileUtilities = FileUtilities;
//# sourceMappingURL=FileUtilities.js.map